import { useEffect } from 'react';
import {
  PHONE_QUERY,
  clearStackedTables,
  updateStackedTables
} from '@/utils/stackedTables';

/**
 * While the screen is phone sized, shows every table on the page (dialogs
 * included) that would scroll sideways as cards: see `utils/stackedTables`.
 * Tables are checked again whenever the page's content or the window size
 * changes: in the mutation callback itself, so before the next paint (no
 * flash of the wide table) and also in a background tab.
 */
export function useStackedTables(): void {
  useEffect(() => {
    if (
      typeof window.matchMedia !== 'function' ||
      typeof MutationObserver === 'undefined'
    ) {
      return undefined;
    }
    const phone = window.matchMedia(PHONE_QUERY);
    let observer: MutationObserver | null = null;

    const run = () => updateStackedTables(document.body, window.innerWidth);
    const start = () => {
      if (observer) return;
      // One callback per batch of DOM changes (a React commit). Our own
      // attribute writes are not observed, so they cause no loop.
      observer = new MutationObserver(run);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
      window.addEventListener('resize', run);
      run();
    };
    const stop = () => {
      observer?.disconnect();
      observer = null;
      window.removeEventListener('resize', run);
      clearStackedTables(document.body);
    };
    const sync = () => (phone.matches ? start() : stop());

    sync();
    phone.addEventListener('change', sync);
    return () => {
      phone.removeEventListener('change', sync);
      stop();
    };
  }, []);
}
